/* eslint-disable no-restricted-syntax */
import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';

import { STATUS } from 'projectConstants';
import apiService from 'services';
import convertToDate from 'utils/getMonthYear';
import flattenIndicatorOutputsForTable from 'utils/flattenIndicatorOutputsForTable';
import patchDimensionalIndicatorsFromUpdate from 'pages/BuilderMode/dataTableV2/patchDimensionalIndicatorsFromUpdate';
import resolveAddIndicatorForDataTable from 'pages/BuilderMode/dataTableV2/resolveAddIndicatorForDataTable';
import mapAddIndicatorResponseToBlockMetadata, {
  mergeIndicatorPatchResponseIntoBlockMetadata,
} from 'utils/mapAddIndicatorResponseToBlockMetadata';

import type { BuilderBlockMetadataResponse } from 'types/builderBlockMetadata';

export const FetchPlannerModeModel = createAsyncThunk(
  'plannerModeModel/get',
  async (modelId: number, thunkAPI) => {
    try {
      const response = await apiService.get_model(modelId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);

export const FetchFormulaList = createAsyncThunk(
  'formulaList/get',
  async (modelId: any, thunkAPI) => {
    try {
      const response = await apiService.get_model_formula_list(modelId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);

export const FetchDataInput = createAsyncThunk(
  'FetchDataInput/get',
  async ({ indicator_id, parameters }: any, thunkAPI) => {
    try {
      const response = await apiService.get_indicator_data_input(indicator_id, {
        params: parameters,
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);

export const FetchPlannerModeBlock = createAsyncThunk(
  'plannerModeBlock/get',
  async (blockId: number, thunkAPI) => {
    try {
      const response = await apiService.get_block(blockId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);

// Metadata fetch (same API used in BuilderModeSlice), kept inside PlannerModeSlice
// so Planner flows can bootstrap from metadata before loading outputs.
export const FetchPlannerBlockMetadata = createAsyncThunk(
  'plannerMode/blockMetadata/fetch',
  async (
    { blockId, parameters }: { blockId: number; parameters?: Record<string, unknown> },
    thunkAPI,
  ) => {
    try {
      const response = await apiService.get_block_metadata(blockId, {
        params: parameters,
      });
      return response.data as BuilderBlockMetadataResponse;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const FetchPlannerModeItems = createAsyncThunk(
  'plannerModeItems/fetch',
  async ({ dimensionId, params, actuals }: any, thunkAPI) => {
    try {
      const response = await apiService.get_dimension_items(dimensionId, { params });
      return { ...response.data, actuals };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const FetchBlockOutputs = createAsyncThunk(
  'blockOutputs/fetch',
  async ({ blockId, params }: any, thunkAPI) => {
    try {
      const response = await apiService.get_block_outputs(blockId, { params });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

// V2 variant (kept separate so legacy flow doesn't break)
export const FetchBlockOutputsV2 = createAsyncThunk(
  'blockOutputs_v2/fetch',
  async ({ blockId, params }: any, thunkAPI) => {
    try {
      const response = await apiService.get_block_outputs_V2(blockId, { params });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const FetchBlockDataValues = createAsyncThunk(
  'blockDataValues/fetch',
  async ({ blockId, params }: any, thunkAPI) => {
    try {
      const response = await apiService.get_block_data_values(blockId, { params });
      return response.data?.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const UpdateIndicator = createAsyncThunk(
  'indicator/patch',
  async (payload: any, thunkAPI) => {
    try {
      const response = await apiService.update_indicator(payload?.indicatorId, payload?.data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const UpdateIndicatorFormat = createAsyncThunk(
  'indicatorFormat/patch',
  async (payload: any, thunkAPI) => {
    try {
      const response = await apiService.update_indicator(payload?.indicatorId, payload?.data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const UpdateIndicatorProperties = createAsyncThunk(
  'indicatorProperties/patch',
  async ({ indicatorId, data }: any, thunkAPI) => {
    try {
      const response = await apiService.update_indicator(indicatorId, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const UpdateIndicatorFormula = createAsyncThunk(
  'indicatorFormula/patch',
  async ({ indicatorId, data }: any, thunkAPI) => {
    try {
      const response = await apiService.update_indicator(indicatorId, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const DuplicateIndicator = createAsyncThunk(
  'duplicateIndicator/post',
  async ({ indicatorId, data }: any, thunkAPI) => {
    try {
      const response = await apiService.duplicate_indicator(indicatorId, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const UpdateBlockDimension = createAsyncThunk(
  'blockDimension/post',
  async ({ blockId, request_body }: any, thunkAPI) => {
    try {
      const response = await apiService.block_dimension(blockId, request_body);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const FetchScenarios = createAsyncThunk(
  'fetchScenarios/fetch',
  async (modelId: any, thunkAPI) => {
    try {
      const response = await apiService.get_scenarios(modelId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const RenameScenario = createAsyncThunk(
  'scenarioRename/fetch',
  async (data: any, thunkAPI) => {
    try {
      const response = await apiService.renameScenario(data?.item_id, data.requestBody);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);

export const DeleteScenarios = createAsyncThunk(
  'scenarioDelete/fetch',
  async (scenarioId: number, thunkAPI) => {
    try {
      const response = await apiService.deleteScenario(scenarioId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const CreateScenarios = createAsyncThunk(
  'scenarioCreate/fetch',
  async (data: any, thunkAPI) => {
    try {
      const response = await apiService.createScenario(data?.modelId, data.data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const DuplicateScenarios = createAsyncThunk(
  'scenarioDuplicate/fetch',
  async (data: any, thunkAPI) => {
    try {
      const response = await apiService.createDuplicateScenario(data?.scenarioId, data.data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const ReorderIndicator = createAsyncThunk(
  'reorderIndicator/fetch',
  async ({ blockId, data }: any, thunkAPI) => {
    try {
      const response = await apiService.reorder_indicator(blockId, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const FetchSingleScenarios = createAsyncThunk(
  'singleScenario/fetch',
  async (scenarioId: number, thunkAPI) => {
    try {
      const response = await apiService.getSingleScenario(scenarioId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const AddIndicator = createAsyncThunk('addIndicator/post', async (data: any, thunkAPI) => {
  try {
    const response = await apiService.add_indicator(data?.blockId, data.data);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error?.response?.data);
  }
});

export const DeleteIndicator = createAsyncThunk(
  'indicator/delete',
  async ({ indicatorId }: any, thunkAPI) => {
    try {
      const response = await apiService.delete_Indicator(indicatorId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const GetDependencyIndicator = createAsyncThunk(
  'indicator/delete',
  async ({ indicatorId }: any, thunkAPI) => {
    try {
      const response = await apiService.get_indicator_dependency(indicatorId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

interface PlannerModeSliceInterface {
  plannerModeSlice: PlannerModeInterface;
}

interface PlannerModeInterface {
  plannerMode: boolean;
  model: any;
  block: any;
  blockMetadata: BuilderBlockMetadataResponse | null;
  blockMetadataScenarioId: number | null;
  blockMetadataLoading: boolean;
  blockMetadataError: string | null;
  indicatorHeader: any;
  dimensionalIndicators: any;
  indicatorsData: any;
  dimensionalIndicatorsV2: any;
  indicatorsDataV2: any;
  selectedCell: any;
  addIndicator: boolean;
  selectedIndicators: any;
  toggleInputPanel: boolean;
  toolbarType: string;
  indicators: any[];
  indicatorsV2: any[];
  scenarioFetchStatus: any;
  blockScenarioMap: any;
  modelId: any;
  blockId: null;
  baseScenario: any;
  scenarioList: any;
  scenarioListStatus: any;
  selectedSceanrioId: any;
  isIndicatorAvailabe: boolean;
  formulaBarChange: any;
  formulaList: any;
  scenarios: any[];
  dataInput: null;
  isExpand: boolean;
  isDependencyArray: any;
  blockOutputsLoading: boolean;
  blockOutputsError: string | null;
  blockOutputsLoadingV2: boolean;
  blockOutputsErrorV2: string | null;
}

const initialState: PlannerModeInterface = {
  plannerMode: localStorage.getItem('BUILDER_MODE') === 'true',
  model: null,
  block: null,
  blockMetadata: null,
  blockMetadataScenarioId: null,
  blockMetadataLoading: false,
  blockMetadataError: null,
  indicatorHeader: null,
  dimensionalIndicators: null,
  indicatorsData: null,
  dimensionalIndicatorsV2: null,
  indicatorsDataV2: null,
  selectedCell: null,
  selectedIndicators: null,
  addIndicator: false,
  toggleInputPanel: false,
  toolbarType: 'Data',
  indicators: [],
  indicatorsV2: [],
  modelId: null,
  blockId: null,
  scenarioFetchStatus: STATUS.IDLE,
  blockScenarioMap: {},
  baseScenario: null,
  scenarioList: null,
  scenarioListStatus: null,
  selectedSceanrioId: null,
  isIndicatorAvailabe: false,
  formulaBarChange: '',
  scenarios: [],
  formulaList: null,
  dataInput: null,
  isExpand: false,
  isDependencyArray: null,
  blockOutputsLoading: false,
  blockOutputsError: null,
  blockOutputsLoadingV2: false,
  blockOutputsErrorV2: null,
};

const PlannerModeSlice = createSlice({
  name: 'plannerModeSlice',
  initialState,

  reducers: {
    handlePlannerMode: (state, action) => {
      localStorage.setItem('BUILDER_MODE', action.payload);
      state.plannerMode = action.payload;
    },
    handleToggleInputPanel: (state) => {
      state.toggleInputPanel = !state.toggleInputPanel;
    },
    toggleToolbarType: (state, action) => {
      state.toolbarType = action.payload;
    },
    handleAddIndicator: (state, action) => {
      state.addIndicator = action.payload;
    },
    handleSelectedCell: (state, action) => {
      state.selectedCell = action.payload;
      const indicators = current(state.indicators) || [];
      const indicatorsV2 = current(state.indicatorsV2) || [];

      let rowId = action.payload?.rowId;
      if (typeof rowId === 'string' && rowId.includes('_')) {
        rowId = Number(rowId.split('_')[1]);
      }

      const matchById = (data: any) => Number(data?.id) === Number(rowId);
      const getSelectedIndicators =
        indicators.find((data: any) => matchById(data)) ??
        indicatorsV2.find((data: any) => matchById(data));

      if (getSelectedIndicators) {
        state.selectedIndicators = getSelectedIndicators;
      }
    },
    handleSelectedCellLocally: (state, action) => {
      state.selectedIndicators = action.payload;
    },
    handleLocallyAddIndicator: (state, action) => {
      const indicators = current(state.indicators) || [];
      const data = [...indicators, action.payload];
      state.indicators = data;
    },
    currentScenario: (state, action) => {
      state.selectedSceanrioId = action.payload;
    },
    indicatorAvailable: (state, action) => {
      state.isIndicatorAvailabe = action.payload;
    },
    selectBaseScenario: (state, action) => {
      state.baseScenario = action.payload;
      state.blockScenarioMap[`${state.modelId}`] = state.baseScenario?.id;
    },
    setExpand: (state, action) => {
      state.isExpand = action.payload;
    },
    resetBuilderDimension: (state) => {
      state.model = null;
      state.block = null;
    },
    ResetTable: (state) => {
      state.model = null;
      state.block = null;
      state.scenarios = [];
      state.indicators = [];
      state.selectedCell = null;
      // state.baseScenario = null;
      state.scenarioList = null;
      state.toolbarType = 'Data';
      state.addIndicator = false;
      state.indicatorsData = null;
      state.formulaBarChange = '';
      state.indicatorHeader = null;
      state.toggleInputPanel = false;
      state.scenarioListStatus = null;
      state.selectedSceanrioId = null;
      state.selectedIndicators = null;
      state.selectedSceanrioId = null;
      state.dimensionalIndicators = null;
      state.isIndicatorAvailabe = false;
      state.formulaList = null;
      state.dataInput = null;
    },
  },

  extraReducers(builder) {
    builder
      .addCase(FetchPlannerModeModel.fulfilled, (state, action) => {
        state.model = action.payload;
      })
      .addCase(FetchPlannerBlockMetadata.pending, (state, action) => {
        const nextScenarioId = action.meta?.arg?.parameters?.scenario_id as number | undefined;
        if (nextScenarioId != null && state.blockMetadataScenarioId !== nextScenarioId) {
          state.blockMetadata = null;
          state.blockMetadataScenarioId = null;
        }
        state.blockMetadataLoading = true;
        state.blockMetadataError = null;
      })
      .addCase(FetchPlannerBlockMetadata.rejected, (state, action: any) => {
        state.blockMetadataLoading = false;
        state.blockMetadataError =
          action?.payload || action?.error?.message || 'Failed to fetch block metadata';
      })
      .addCase(FetchPlannerBlockMetadata.fulfilled, (state, action) => {
        state.blockMetadataLoading = false;
        state.blockMetadataError = null;
        state.blockMetadata = action.payload;
        state.blockMetadataScenarioId =
          (action.meta?.arg?.parameters?.scenario_id as number | undefined) ?? null;
      })
      .addCase(FetchDataInput.fulfilled, (state, action) => {
        state.dataInput = action?.payload;
      })
      .addCase(FetchScenarios.fulfilled, (state, action) => {
        state.scenarios = action.payload.scenarios;
        state.scenarioFetchStatus = STATUS.SUCCESS;
        const userSpecificScenario = state.blockScenarioMap[`${state?.modelId}`];
        if (userSpecificScenario && state?.blockId) {
          state.baseScenario =
            state.scenarios &&
            state.scenarios.find((scenario: any) => {
              return scenario?.id === userSpecificScenario;
            });
        } else if (!state.baseScenario) {
          state.baseScenario =
            state.scenarios &&
            state.scenarios.find((scenario: any) => {
              return scenario.is_base;
            });
          if (state?.modelId) state.blockScenarioMap[`${state.modelId}`] = state.baseScenario?.id;
        }
      })
      .addCase(FetchScenarios.rejected, (state) => {
        state.scenarios = [];
      })
      .addCase(UpdateBlockDimension.fulfilled, (state, action) => {
        const block = current(state?.block) || [];
        const updatedDimension = { ...block, dimensions: action.payload?.dimensions };
        state.block = updatedDimension;
      })
      .addCase(FetchPlannerModeBlock.fulfilled, (state, action) => {
        state.block = action.payload;
      })
      .addCase(FetchPlannerModeItems.fulfilled, (state, action) => {
        const actualsData = action.payload?.items?.map((item: any) => {
          let isActual = false;
          if (
            action.payload.actuals[0] + action.payload.actuals[1] === 'FY' &&
            Number(action.payload.actuals[2] + action.payload.actuals[3]) + 2000 >=
              Number(item?.name)
          ) {
            isActual = true;
          } else if (convertToDate(action.payload?.actuals) >= convertToDate(item?.name)) {
            isActual = true;
          }
          return { ...item, isActual };
        });
        state.indicatorHeader = actualsData;
      })
      .addCase(UpdateIndicator.fulfilled, (state, action) => {
        const payload = action.payload as Record<string, any> | undefined;
        if (!payload) return;

        const patchId = Number(payload.id ?? action.meta?.arg?.indicatorId);
        if (!Number.isNaN(patchId)) {
          if (state.blockMetadata?.indicators?.length) {
            const metaIndex = state.blockMetadata.indicators.findIndex(
              (indicator: { id: number }) => Number(indicator.id) === patchId,
            );
            if (metaIndex !== -1) {
              state.blockMetadata.indicators[metaIndex] =
                mergeIndicatorPatchResponseIntoBlockMetadata(
                  state.blockMetadata.indicators[metaIndex],
                  payload,
                );
            }
          }

          if (state.dimensionalIndicatorsV2?.length) {
            state.dimensionalIndicatorsV2 = patchDimensionalIndicatorsFromUpdate(
              state.dimensionalIndicatorsV2,
              patchId,
              payload,
            );
            if (Number(state.selectedIndicators?.id) === patchId) {
              const parentRow = state.dimensionalIndicatorsV2.find(
                (row: any) => row?.chevronType !== 'child' && Number(row?.id) === patchId,
              );
              state.selectedIndicators = parentRow
                ? {
                    ...state.selectedIndicators,
                    ...parentRow,
                    name: payload?.name ?? parentRow.name ?? state.selectedIndicators?.name,
                  }
                : {
                    ...state.selectedIndicators,
                    name: payload?.name ?? state.selectedIndicators?.name,
                  };
            }
          } else if (Number(state.selectedIndicators?.id) === patchId && payload?.name != null) {
            state.selectedIndicators = {
              ...state.selectedIndicators,
              name: payload.name,
            };
          }
        }

        // Legacy output indicator list
        const indicators = current(state.indicators) || [];
        const index = indicators.findIndex((data: any) => data?.id === payload?.id);
        if (index !== -1) {
          const newIndicator = {
            ...state.indicators[index],
            chart_type: payload?.chart_type,
            data_type: payload?.data_type,
            format: payload?.data_format,
            formula: payload?.formula,
            help: payload?.help,
            indicator_type:
              payload?.indicator_type ?? payload?.type ?? state.indicators[index]?.indicator_type,
            is_favourite: payload?.favourite,
            name: payload?.name,
            output_format: payload?.output_format,
            type: payload?.data_format,
          };
          state.indicators[index] = newIndicator;
          if (Number(state.selectedIndicators?.id) === patchId) {
            state.selectedIndicators = {
              ...state.selectedIndicators,
              ...newIndicator,
            };
          }
        }

        // Updates Data view for table
        const indicatorsData = current(state.indicatorsData) || [];
        const indicatorIndex = indicatorsData.findIndex((data: any) => data?.id === payload?.id);
        if (indicatorIndex !== -1) {
          const indicatorPayload = {
            id: payload?.id,
            warning: payload?.warning,
            type: indicatorsData[indicatorIndex]?.type,
            'Block indicators': payload?.name,
            Type: payload?.type,
            Formula: payload?.formula ?? '',
            'Help text': payload?.help ?? '',
            Format: payload?.data_format,
            indicator_type: payload?.type,
            'Roll up type': payload?.data_type,
            'Display as output': payload?.favourite,
            'Display as sub-total': payload?.output_format,
            'Default chart type': payload?.chart_type,
          };
          state.indicatorsData[indicatorIndex] = indicatorPayload;
        }
        const blockOutputs: any = current(state.indicators) || [];
        const flattenedArray = [];
        for (const object of blockOutputs) {
          if (object?.dim_item_data) {
            flattenedArray.push({ ...object, chevronType: 'parent' });
          } else {
            flattenedArray.push(object);
          }
          if (object?.dim_item_data) {
            for (const dataObject of object.dim_item_data) {
              const childObject = {
                ...dataObject,
                type: object?.type,
                chevronType: 'child',
                total_values: dataObject?.value,
                indicatorId: object?.id,
                parentId: object?.id,
              };
              flattenedArray.push(childObject);
            }
          }
        }
        state.dimensionalIndicators = flattenedArray;
      })
      .addCase(UpdateIndicatorFormat.fulfilled, (state, action) => {
        // Update indicator for output indicator list
        const { payload } = action;
        const indicators = current(state.indicators) || [];
        const index = indicators.findIndex((data: any) => data?.id === payload?.id);
        if (index !== -1) {
          const newIndicator = {
            ...state.indicators[index],
            chart_type: payload?.chart_type,
            data_type: payload?.data_type,
            format: payload?.data_format,
            formula: payload?.formula,
            help: payload?.help,
            indicator_type: payload?.type,
            is_favourite: payload?.favourite,
            name: payload?.name,
            output_format: payload?.output_format,
            type: payload?.data_format,
          };
          state.indicators[index] = newIndicator;
          state.selectedIndicators = newIndicator;
        }
        // Updates Data view for table
        // const indicatorsData = current(state.indicatorsData) || [];
        // const indicatorIndex = indicatorsData.findIndex((data: any) => data?.id === payload?.id);
        // if (indicatorIndex !== -1) {
        //   const indicatorPayload = {
        //     id: payload?.id,
        //     warning: payload?.warning,
        //     type: indicatorsData[indicatorIndex]?.type,
        //     'Block indicators': payload?.name,
        //     Type: payload?.type,
        //     Formula: payload?.formula ?? '',
        //     'Help text': payload?.help ?? '',
        //     Format: payload?.data_format,
        //     indicator_type: payload?.type,
        //     'Roll up type': payload?.data_type,
        //     'Display as output': payload?.favourite,
        //     'Display as sub-total': payload?.output_format,
        //     'Default chart type': payload?.instrument_type,
        //   };
        //   state.indicatorsData[indicatorIndex] = indicatorPayload;
        // }
        // const blockOutputs: any = current(state.indicators) || [];
        // const flattenedArray = [];
        // for (const object of blockOutputs) {
        //   if (object?.dim_item_data) {
        //     flattenedArray.push({ ...object, chevronType: 'parent' });
        //   } else {
        //     flattenedArray.push(object);
        //   }
        //   if (object?.dim_item_data) {
        //     for (const dataObject of object.dim_item_data) {
        //       const childObject = {
        //         ...dataObject,
        //         type: object?.type,
        //         chevronType: 'child',
        //         total_values: dataObject?.value,
        //         indicatorId: object?.id,
        //         parentId: object?.id,
        //       };
        //       flattenedArray.push(childObject);
        //     }
        //   }
        // }
        // state.dimensionalIndicators = flattenedArray;
      })
      .addCase(FetchBlockOutputs.pending, (state) => {
        state.blockOutputsLoading = true;
        state.blockOutputsError = null;
      })
      .addCase(FetchBlockOutputs.rejected, (state, action) => {
        state.blockOutputsLoading = false;
        state.blockOutputsError = action.error.message || 'Failed to fetch block outputs';
      })
      .addCase(FetchBlockOutputs.fulfilled, (state, action) => {
        state.blockOutputsLoading = false;
        state.blockOutputsError = null;
        state.indicators = action.payload;
        const indicatorsArray: any[] = [];
        action.payload.forEach((data: any) => {
          const indicator = {
            id: data?.id,
            warning: data?.warning,
            type: data?.type,
            'Block indicators': data?.name,
            Type: data?.indicator_type,
            Formula: data?.formula ?? '',
            indicator_type: data?.indicator_type,
            'Help text': data?.help ?? '',
            Format: data?.format,
            'Roll up type': data?.data_type,
            'Display as output': data?.is_favourite,
            'Display as sub-total': data?.output_format,
            'Default chart type': data?.chart_type,
          };
          indicatorsArray.push(indicator);
        });
        state.indicatorsData = indicatorsArray;

        const blockOutputs: any = action.payload;
        const flattenedArray = [];
        for (const object of blockOutputs) {
          if (object?.dim_item_data) {
            flattenedArray.push({ ...object, chevronType: 'parent' });
          } else {
            flattenedArray.push(object);
          }
          if (object?.dim_item_data) {
            const dimItems = Array.isArray(object.dim_item_data)
              ? object.dim_item_data
              : Object.values(object.dim_item_data || {});
            for (const dataObject of dimItems) {
              const childObject = {
                ...dataObject,
                type: object?.type,
                chevronType: 'child',
                total_values: dataObject?.value,
                indicatorId: object?.id,
                parentId: object?.id,
              };
              flattenedArray.push(childObject);
            }
          }
        }
        state.dimensionalIndicators = flattenedArray;
        if (state.selectedIndicators) {
          const selectedIndicatorIndex = state.dimensionalIndicators.findIndex(
            (data: any) => data?.id === state.selectedIndicators?.id,
          );
          if (selectedIndicatorIndex !== -1) {
            // state.selectedIndicators = state.dimensionalIndicators[selectedIndicatorIndex];
          }
        }
      })
      .addCase(FetchBlockOutputsV2.pending, (state) => {
        state.blockOutputsLoadingV2 = true;
        state.blockOutputsErrorV2 = null;
      })
      .addCase(FetchBlockOutputsV2.rejected, (state, action) => {
        state.blockOutputsLoadingV2 = false;
        state.blockOutputsErrorV2 = action.error.message || 'Failed to fetch block outputs';
      })
      .addCase(FetchBlockOutputsV2.fulfilled, (state, action) => {
        state.blockOutputsLoadingV2 = false;
        state.blockOutputsErrorV2 = null;
        state.indicatorsV2 = action.payload;

        const indicatorsArray: any[] = [];
        action.payload.forEach((data: any) => {
          const indicator = {
            id: data?.id,
            warning: data?.warning,
            type: data?.type,
            'Block indicators': data?.name,
            Type: data?.indicator_type,
            Formula: data?.formula ?? '',
            indicator_type: data?.indicator_type,
            'Help text': data?.help ?? '',
            Format: data?.format,
            'Roll up type': data?.data_type,
            'Display as output': data?.is_favourite,
            'Display as sub-total': data?.output_format,
            'Default chart type': data?.chart_type,
          };
          indicatorsArray.push(indicator);
        });
        state.indicatorsDataV2 = indicatorsArray;

        const blockOutputs: any = action.payload;
        const flattenedArray = blockOutputs.flatMap((object: Record<string, any>) =>
          flattenIndicatorOutputsForTable(object),
        );
        state.dimensionalIndicatorsV2 = flattenedArray;
      })
      .addCase(AddIndicator.fulfilled, (state, action) => {
        const raw = action.payload as Record<string, any> | undefined;
        if (!raw?.id) return;

        const requestedBlockId = action.meta?.arg?.blockId;
        if (
          requestedBlockId != null &&
          raw.block_id != null &&
          Number(requestedBlockId) !== Number(raw.block_id)
        ) {
          return;
        }

        const headers = (state.indicatorHeader ?? []).map((header: any) => ({
          name: header?.name ?? '',
        }));

        if (!state.blockMetadata) {
          state.blockMetadata = { indicators: [] };
        }
        if (!state.blockMetadata.indicators) {
          state.blockMetadata.indicators = [];
        }
        const existsInMetadata = state.blockMetadata.indicators.some(
          (indicator: { id: number }) => indicator.id === raw.id,
        );
        if (!existsInMetadata) {
          state.blockMetadata.indicators.push(mapAddIndicatorResponseToBlockMetadata(raw));
        }

        const existingOutputs = state.dimensionalIndicatorsV2 ?? [];
        const newDimensionalRows = resolveAddIndicatorForDataTable(
          raw,
          headers,
          existingOutputs,
          state.block,
        );
        const outputs = existingOutputs;
        const existsInOutputs = outputs.some(
          (row: any) => row?.id === raw.id && row?.chevronType !== 'child' && !row?.parentId,
        );
        if (!existsInOutputs) {
          state.dimensionalIndicatorsV2 = [...outputs, ...newDimensionalRows];
        }

        const parentFlatRow =
          newDimensionalRows.find((row: any) => row?.chevronType !== 'child') ??
          newDimensionalRows[0];
        if (!parentFlatRow) return;

        const parentOutput = { ...parentFlatRow };
        delete parentOutput.chevronType;
        delete parentOutput.indicatorId;
        delete parentOutput.parentId;

        const blockDimensionIds =
          state.block?.dimensions
            ?.filter((dimension: { name: string }) => dimension.name !== 'Time')
            ?.map((dimension: { id: number }) => dimension.id) ?? [];
        parentOutput.data_input = {
          ...parentOutput.data_input,
          block_id:
            parentOutput.data_input?.block_id ??
            raw.block_id ??
            (requestedBlockId != null ? Number(requestedBlockId) : undefined),
          dimensions:
            blockDimensionIds.length > 0
              ? blockDimensionIds
              : parentOutput.data_input?.dimensions ?? [],
        };

        const indicatorId = raw.id;

        const existsInIndicatorsV2 = (state.indicatorsV2 ?? []).some(
          (indicator: { id: number }) => indicator.id === indicatorId,
        );
        if (!existsInIndicatorsV2) {
          state.indicatorsV2 = [...(state.indicatorsV2 ?? []), parentOutput];
        }

        const existsInIndicators = (state.indicators ?? []).some(
          (indicator: { id: number }) => indicator.id === indicatorId,
        );
        if (!existsInIndicators) {
          state.indicators = [...(state.indicators ?? []), parentOutput];
        }

        const indicatorsDataEntry = {
          id: parentOutput?.id,
          warning: parentOutput?.warning,
          type: parentOutput?.type,
          'Block indicators': parentOutput?.name,
          Type: parentOutput?.indicator_type,
          Formula: parentOutput?.formula ?? '',
          indicator_type: parentOutput?.indicator_type,
          'Help text': parentOutput?.help ?? '',
          Format: parentOutput?.format,
          'Roll up type': parentOutput?.data_type,
          'Display as output': parentOutput?.is_favourite,
          'Display as sub-total': parentOutput?.output_format,
          'Default chart type': parentOutput?.chart_type,
        };
        const existsInIndicatorsDataV2 = (state.indicatorsDataV2 ?? []).some(
          (entry: { id: number }) => entry.id === indicatorId,
        );
        if (!existsInIndicatorsDataV2) {
          state.indicatorsDataV2 = [...(state.indicatorsDataV2 ?? []), indicatorsDataEntry];
        }

        state.selectedIndicators = parentOutput;
      })
      .addCase(GetDependencyIndicator.fulfilled, (state, action) => {
        state.isDependencyArray = action.payload;
      })
      .addCase(FetchFormulaList.fulfilled, (state, action) => {
        state.formulaList = action.payload;
      })
      .addCase(UpdateIndicatorFormula.fulfilled, (state, action) => {
        const indicators = current(state.indicators) || [];
        const data = action.payload;
        // Find the index of the item to update
        const index = indicators?.findIndex((item: any) => item?.id === data?.id);
        // If found, update only the calculated_formula key
        if (index !== -1) {
          state.indicators[index] = {
            ...state.indicators[index],
            calculated_formula: { formula_map: data?.calculate_formula, raw_parsed_formula: '' },
            formula: data?.formula,
          };
        }
      });
  },
});

export const getPlannerMode = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.plannerMode;
export const getPlannerModel = (state: PlannerModeSliceInterface) => state.plannerModeSlice.model;
export const getPlannerBlock = (state: PlannerModeSliceInterface) => state.plannerModeSlice.block;
export const getPlannerBlockMetadata = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.blockMetadata;
export const getPlannerBlockMetadataScenarioId = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.blockMetadataScenarioId;
export const getPlannerBlockMetadataLoading = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.blockMetadataLoading;
export const getPlannerBlockMetadataError = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.blockMetadataError;
export const getIndicatorHeaders = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.indicatorHeader;
export const getDimensionalIndicator = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.dimensionalIndicators;
export const getDimensionalIndicatorV2 = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.dimensionalIndicatorsV2;
export const getIndicatorsData = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.indicatorsData;
export const getIndicatorsDataV2 = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.indicatorsDataV2;
export const getIndicators = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.indicators;
export const getIndicatorsV2 = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.indicatorsV2;
export const getSelectedCell = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.selectedCell;
export const getSelectedIndicators = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.selectedIndicators;
export const getAddIndicator = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.addIndicator;
export const getToggleInputPanel = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.toggleInputPanel;
export const getToolbarType = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice.toolbarType;
export const getAllScenarios = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.scenarios;
export const getBaseScenario = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.baseScenario;
export const getFormulaBarStatus = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.formulaBarChange;
export const getDataInput = (state: PlannerModeSliceInterface) => state.plannerModeSlice?.dataInput;
export const getFormulaList = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.formulaList;
export const getExpandValue = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.isExpand;
export const getDependencyArray = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.isDependencyArray;
export const getBlockOutputsLoading = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.blockOutputsLoading;
export const getBlockOutputsError = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.blockOutputsError;
export const getBlockOutputsLoadingV2 = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.blockOutputsLoadingV2;
export const getBlockOutputsErrorV2 = (state: PlannerModeSliceInterface) =>
  state.plannerModeSlice?.blockOutputsErrorV2;

export const {
  ResetTable,
  toggleToolbarType,
  handlePlannerMode,
  handleSelectedCell,
  handleAddIndicator,
  selectBaseScenario,
  handleToggleInputPanel,
  setExpand,
  handleLocallyAddIndicator,
  handleSelectedCellLocally,
} = PlannerModeSlice.actions;

export default PlannerModeSlice.reducer;
